import * as React from 'react';

export const CHIP_CLOSE_DELAY_MS = 300;

interface Measurable {
	getBoundingClientRect(): DOMRect;
}

interface UseChipPopoverOptions {
	/** The element the editor renders its content into. */
	root: HTMLElement | null | undefined;
	/** The editor's iframe, when the content lives in one, so the anchor can be placed in page coordinates. */
	frame?: HTMLElement | null;
	chipSelector: string;
	isEditorFocused: () => boolean;
	/** Called each time an open popover closes, however it was dismissed. */
	onClose?: () => void;
}

/**
 * Opens a popover over a merge-field chip while the pointer is on the chip or the popover, or while a field
 * inside the popover has focus. Also opens when the sender inserts a new chip, and closes once they go back to
 * typing or clicking in the editor.
 */
export function useChipPopover({ root, frame, chipSelector, isEditorFocused, onClose }: UseChipPopoverOptions) {
	const [chip, setChip] = React.useState<Element | null>(null);
	const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const hasFocusWithinRef = React.useRef(false);
	const latestRef = React.useRef({ frame, isEditorFocused, onClose });
	latestRef.current = { frame, isEditorFocused, onClose };
	const wasOpenRef = React.useRef(false);

	React.useEffect(() => {
		if (wasOpenRef.current && !chip) {
			latestRef.current.onClose?.();
		}
		wasOpenRef.current = !!chip;
	}, [chip]);

	const anchorRef = React.useRef<Measurable>(null);
	anchorRef.current = chip
		? {
				getBoundingClientRect: () => {
					const chipRect = chip.getBoundingClientRect();
					const frameRect = latestRef.current.frame?.getBoundingClientRect();
					return frameRect
						? new DOMRect(chipRect.x + frameRect.x, chipRect.y + frameRect.y, chipRect.width, chipRect.height)
						: chipRect;
				},
			}
		: null;

	const cancelClose = React.useCallback(() => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	}, []);

	const close = React.useCallback(() => {
		cancelClose();
		hasFocusWithinRef.current = false;
		setChip(null);
	}, [cancelClose]);

	const scheduleClose = React.useCallback(() => {
		cancelClose();
		closeTimerRef.current = setTimeout(() => {
			if (!hasFocusWithinRef.current) {
				setChip(null);
			}
		}, CHIP_CLOSE_DELAY_MS);
	}, [cancelClose]);

	const openOn = React.useCallback(
		(nextChip: Element) => {
			cancelClose();
			setChip(nextChip);
		},
		[cancelClose]
	);

	React.useEffect(() => {
		if (!root) {
			return;
		}
		// The editor's iframe has its own Element constructor, so check for `closest` rather than `instanceof`.
		const chipFrom = (target: EventTarget | null) => {
			const found = (target as Element | null)?.closest?.(chipSelector);
			return found && root.contains(found) ? found : null;
		};
		const countChips = () => root.querySelectorAll(chipSelector).length;

		const onMouseOver = (e: MouseEvent) => {
			const hovered = chipFrom(e.target);
			if (hovered) {
				openOn(hovered);
			}
		};
		const onMouseOut = (e: MouseEvent) => {
			if (chipFrom(e.target) && !chipFrom(e.relatedTarget)) {
				scheduleClose();
			}
		};
		// Radix can't see pointer events inside the editor's iframe, so typing or clicking away from the chip
		// there is what dismisses a popover the sender never moved into.
		const onEditorInteraction = (e: Event) => {
			if (!chipFrom(e.target)) {
				close();
			}
		};

		let chipCount = countChips();
		const observer = new MutationObserver(mutations => {
			const nextCount = countChips();
			const added = nextCount > chipCount;
			chipCount = nextCount;
			setChip(current => (current && !current.isConnected ? null : current));
			if (!added || !latestRef.current.isEditorFocused()) {
				return;
			}
			const newChip = mutations
				.flatMap(m => Array.from(m.addedNodes) as Element[])
				.map(node => (node.matches?.(chipSelector) ? node : node.querySelector?.(chipSelector)))
				.find(Boolean);
			if (newChip) {
				openOn(newChip);
			}
		});

		root.addEventListener('mouseover', onMouseOver);
		root.addEventListener('mouseout', onMouseOut);
		root.addEventListener('keydown', onEditorInteraction);
		root.addEventListener('mousedown', onEditorInteraction);
		observer.observe(root, { childList: true, subtree: true });
		return () => {
			root.removeEventListener('mouseover', onMouseOver);
			root.removeEventListener('mouseout', onMouseOut);
			root.removeEventListener('keydown', onEditorInteraction);
			root.removeEventListener('mousedown', onEditorInteraction);
			observer.disconnect();
		};
	}, [root, chipSelector, openOn, scheduleClose, close]);

	React.useEffect(() => cancelClose, [cancelClose]);

	return {
		isOpen: !!chip,
		anchorRef,
		close,
		contentProps: {
			onMouseEnter: cancelClose,
			onMouseLeave: scheduleClose,
			onFocus: () => {
				hasFocusWithinRef.current = true;
				cancelClose();
			},
			onBlur: (e: React.FocusEvent<HTMLElement>) => {
				if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
					return;
				}
				hasFocusWithinRef.current = false;
				scheduleClose();
			},
		},
	};
}
